import * as React from 'react';

import {
  initializeGoogleAnalytics,
  isGoogleAnalyticsEnabled,
  sanitizePageLocation,
  trackGoogleAnalyticsPageView,
} from './googleAnalytics';

import {
  DISABLED_ANALYTICS_CONFIG,
  getAnalyticsConfig,
} from '~/lib/config/env';

interface AnalyticsProviderProps {
  children: React.ReactNode;
}

export const AnalyticsProvider = ({ children }: AnalyticsProviderProps) => {
  const [analyticsConfig] = React.useState(() => {
    if (typeof window === 'undefined') {
      return DISABLED_ANALYTICS_CONFIG;
    }

    return getAnalyticsConfig(false);
  });
  const lastTrackedPageLocationRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!isGoogleAnalyticsEnabled(analyticsConfig)) {
      return;
    }

    initializeGoogleAnalytics(analyticsConfig);
  }, [analyticsConfig]);

  const trackCurrentPage = React.useCallback(() => {
    const pageLocation = sanitizePageLocation(window.location.href);
    if (lastTrackedPageLocationRef.current === pageLocation) {
      return;
    }

    lastTrackedPageLocationRef.current = pageLocation;
    trackGoogleAnalyticsPageView(
      analyticsConfig,
      pageLocation,
      document.title
    );
  }, [analyticsConfig]);

  React.useEffect(() => {
    if (!isGoogleAnalyticsEnabled(analyticsConfig)) {
      return;
    }

    const schedulePageTracking = () => {
      window.setTimeout(trackCurrentPage, 0);
    };
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    trackCurrentPage();

    window.history.pushState = function pushState(
      ...args: Parameters<History['pushState']>
    ) {
      originalPushState.apply(window.history, args);
      schedulePageTracking();
    } as History['pushState'];

    window.history.replaceState = function replaceState(
      ...args: Parameters<History['replaceState']>
    ) {
      originalReplaceState.apply(window.history, args);
      schedulePageTracking();
    } as History['replaceState'];

    window.addEventListener('popstate', schedulePageTracking);

    return () => {
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
      window.removeEventListener('popstate', schedulePageTracking);
    };
  }, [analyticsConfig, trackCurrentPage]);

  return children;
};
